import { useCallback, useEffect, useMemo } from 'react';
import InterestsIcon from '@mui/icons-material/Interests';
import { Container, Grid, Stack } from '@mui/material';
import { Box } from '@mui/system';
// react icons
import { BsTelephone } from 'react-icons/bs';
import { FaRegUser } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { GiModernCity, GiSwapBag } from 'react-icons/gi';
import { HiOutlineMail } from 'react-icons/hi';
// components
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import Scrollbar from '../../components/Scrollbar';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import useDisburseSelector from '../../store/Slice/Disburse/DisburseSelector';
import { getDisburseViewData } from '../../store/Slice/Disburse/DisburseSlice';
import ViewCardComponent from '../../components/ViewCards';
import { normalizeValue } from '../../utils/CommonFunction';

function ViewDisburse() {
  const dispatch = useDispatch();
  const location = useLocation();
  const rowId = location.state || {};

  const { LoginData } = useLoginSelector();
  const { DisburseViewData } = useDisburseSelector();

  const getDisbirseViewAPi = useCallback(() => {
    const finalObject = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      applicationId: rowId?.applicationId,
      stageId: 58
    };
    dispatch(getDisburseViewData(finalObject));
  }, [LoginData, rowId]);

  useEffect(() => {
    getDisbirseViewAPi();
  }, [getDisbirseViewAPi]);

  const loanRequirementFields = useMemo(() => {
    const data = DisburseViewData?.disbursedApplicationEntityLoanRequirementResponseModel;
    if (!data) return [];

    return [
      {
        label: 'Name',
        value: normalizeValue(data.leadName),
        icon: <FaRegUser className="viewLabelIcon" size={24} />
      },
      {
        label: 'Phone',
        value: normalizeValue(data.leadPhoneNumber),
        icon: <BsTelephone className="viewLabelIcon" size={24} />
      },
      {
        label: 'Loan Type',
        value: normalizeValue(data.loanType),
        icon: <InterestsIcon className="viewLabelIcon" size={24} />
      },
      {
        label: 'Employment Type',
        value: normalizeValue(data.employmentType),
        icon: <InterestsIcon className="viewLabelIcon" size={24} />
      },
      {
        label: 'Loan Amount',
        value: normalizeValue(data.loanAmount),
        icon: <GiSwapBag className="viewLabelIcon" size={24} />
      },
      {
        label: 'City',
        value: normalizeValue(data.city),
        icon: <GiModernCity className="viewLabelIcon" size={24} />
      },
      {
        label: 'Email',
        value: normalizeValue(data.leadEmail),
        icon: <HiOutlineMail className="viewLabelIcon" size={24} />
      }
    ];
  }, [DisburseViewData?.disbursedApplicationEntityLoanRequirementResponseModel]);

  // Memoized fields for Submission Details
  const submissionDetailsFields = useMemo(() => {
    const data = DisburseViewData?.disbursedApplicationEntitySubmissionDetailsResponseModel;
    if (!data) return [];

    return [
      {
        label: 'Bank Name',
        value: normalizeValue(data.bankName),
        icon: <FaRegUser className="viewLabelIcon" size={24} />
      },
      {
        label: 'Mode of Submission',
        value: normalizeValue(data.modeOfSubmission),
        icon: <InterestsIcon className="viewLabelIcon" size={24} />
      },
      {
        label: 'Processing Center',
        value: normalizeValue(data.processingCentre),
        icon: <InterestsIcon className="viewLabelIcon" size={24} />
      },
      {
        label: 'Person Name',
        value: normalizeValue(data.personName),
        icon: <GiSwapBag className="viewLabelIcon" size={24} />
      },
      {
        label: 'Submitted On',
        value: normalizeValue(data.submittedOn),
        icon: <GiModernCity className="viewLabelIcon" size={24} />
      },
      {
        label: 'Comments',
        value: normalizeValue(data.comments),
        icon: <HiOutlineMail className="viewLabelIcon" size={24} />
      }
    ];
  }, [DisburseViewData?.disbursedApplicationEntitySubmissionDetailsResponseModel]);

  const LoginProfile = useMemo(() => {
    const data = DisburseViewData?.disbursedApplicationEntityLoginProfileResponseModel;
    if (!data) return [];

    return [
      {
        label: 'companyName',
        value: normalizeValue(data.companyName),
        icon: <FaRegUser className="viewLabelIcon" size={24} />
      },
      {
        label: 'dsaName',
        value: normalizeValue(data.dsaName),
        icon: <InterestsIcon className="viewLabelIcon" size={24} />
      },
      {
        label: 'lanId',
        value: normalizeValue(data.lanId),
        icon: <InterestsIcon className="viewLabelIcon" size={24} />
      },
      {
        label: 'sourceType',
        value: normalizeValue(data.sourceType),
        icon: <GiSwapBag className="viewLabelIcon" size={24} />
      },
      {
        label: 'currentStage',
        value: normalizeValue(data.currentStage),
        icon: <GiModernCity className="viewLabelIcon" size={24} />
      },
      {
        label: 'currentStatus',
        value: normalizeValue(data.currentStatus),
        icon: <HiOutlineMail className="viewLabelIcon" size={24} />
      },

      {
        label: 'sourceFlag',
        value: normalizeValue(data.sourceFlag),
        icon: <HiOutlineMail className="viewLabelIcon" size={24} />
      },
      {
        label: 'loginProfileId',
        value: normalizeValue(data.loginProfileId),
        icon: <HiOutlineMail className="viewLabelIcon" size={24} />
      }
    ];
  }, [DisburseViewData?.disbursedApplicationEntityLoginProfileResponseModel]);

  const DisbursementInfo = useMemo(() => {
    const data = DisburseViewData?.disbursedApplicationEntityDisbursementInfoResponseModel;
    if (!data) return [];

    return [
      {
        label: 'disbursedAmount',
        value: normalizeValue(data.disbursedAmount),
        icon: <FaRegUser className="viewLabelIcon" size={24} />
      },
      {
        label: 'disbursedDate',
        value: normalizeValue(data.disbursedDate),
        icon: <InterestsIcon className="viewLabelIcon" size={24} />
      },
      {
        label: 'disbursementDocFlag',
        value: normalizeValue(data.disbursementDocFlag),
        icon: <InterestsIcon className="viewLabelIcon" size={24} />
      },
      {
        label: 'encashmentApprovedFlag',
        value: normalizeValue(data.encashmentApprovedFlag),
        icon: <GiSwapBag className="viewLabelIcon" size={24} />
      },
      {
        label: 'payoutPercent',
        value: normalizeValue(data.payoutPercent),
        icon: <GiModernCity className="viewLabelIcon" size={24} />
      },
      {
        label: 'brokerPayoutPercent',
        value: normalizeValue(data.brokerPayoutPercent),
        icon: <HiOutlineMail className="viewLabelIcon" size={24} />
      },
      {
        label: 'brokerName',
        value: normalizeValue(data.brokerName),
        icon: <HiOutlineMail className="viewLabelIcon" size={24} />
      }
    ];
  }, [DisburseViewData?.disbursedApplicationEntityDisbursementInfoResponseModel]);

  return (
    <Page title="Partner Management System || Disburse">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="Application Progress Report" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>

          <Scrollbar>
            <Grid container spacing={3} mb={3}>
              {DisburseViewData?.disbursedApplicationEntityLoanRequirementResponseModel && (
                <ViewCardComponent title="Loan Requirement" fields={loanRequirementFields} />
              )}
              {DisburseViewData?.disbursedApplicationEntitySubmissionDetailsResponseModel && (
                <ViewCardComponent title="Submission Details" fields={submissionDetailsFields} />
              )}

              {DisburseViewData?.disbursedApplicationEntityLoginProfileResponseModel && (
                <ViewCardComponent title="Submission Details" fields={LoginProfile} />
              )}

              {DisburseViewData?.disbursedApplicationEntityDisbursementInfoResponseModel && (
                <ViewCardComponent title="Submission Details" fields={DisbursementInfo} />
              )}
            </Grid>
          </Scrollbar>
        </Box>
      </Container>
    </Page>
  );
}
export default ViewDisburse;
